import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { translate } from '../../../../../Services/translation';
import TrackingContainer from '../../../../../Containers/Tracking.container';
import { cn } from '../../../../../Utils/cn';
import { update as updateOrderRowQuantity } from '../../../../../Actions/Cart.action';
import { reloadPayment } from '../../../../../Actions/Checkout.action';
import useScrollable from '../../../../../Hooks/useScrollable';
import {
  isKlarnaPayment,
  klarnaResume,
  klarnaSuspend,
} from '../../../../../Utils/klarna';

const MIN_QUANTITY = 0;
const MAX_QUANTITY = 999;

const CheckoutCartItems = ({ checkout, cart }) => {
  const { payload, isSubmitting } = checkout || {};
  const { isBusinessCustomer, selectedPaymentMethod } = payload || {};

  const { orderRows } = cart;

  const dispatch = useDispatch();

  const [editingQuantities, setEditingQuantities] = useState({});
  const [loading, setLoading] = useState(false);

  const {
    ref: listRef,
    isScrollable,
    isAtTop,
    isAtBottom,
  } = useScrollable([orderRows]);

  const handleIncreaseQuantity = (orderRow) => {
    const quantity = orderRow?.quantity;
    if (quantity + 1 <= MAX_QUANTITY) {
      updateQuantity(orderRow, quantity + 1);
    }
  };

  const handleReduceQuantity = (orderRow) => {
    const quantity = orderRow?.quantity;
    if (quantity - 1 >= MIN_QUANTITY) {
      updateQuantity(orderRow, quantity - 1);
    }
  };

  const handleRemoveOrderRow = (orderRow) => updateQuantity(orderRow, 0);

  const clearEditingQuantity = (rowId) =>
    setEditingQuantities((previous) => {
      const newState = { ...previous };
      delete newState[rowId];
      return newState;
    });

  const updateQuantity = async (orderRow, updatedQuantity) => {
    const quantity = Math.max(
      MIN_QUANTITY,
      Math.min(MAX_QUANTITY, updatedQuantity)
    );
    if (quantity === orderRow.quantity) {
      clearEditingQuantity(orderRow.rowSystemId);
      return;
    }
    setLoading(true);
    if (isKlarnaPayment(selectedPaymentMethod)) {
      klarnaSuspend();
    }
    try {
      await dispatch(updateOrderRowQuantity(orderRow.rowSystemId, quantity));
      await dispatch(reloadPayment());
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
    clearEditingQuantity(orderRow.rowSystemId);
    setLoading(false);
    if (isKlarnaPayment(selectedPaymentMethod)) {
      klarnaResume();
    }
  };

  TrackingContainer({
    tracking: 'checkout',
    data: {
      orderRows,
    },
  });

  if (!orderRows?.length) {
    return null;
  }

  return (
    <ul
      className={cn(
        'mb-4 border-y border-transparent py-4 lg:min-h-48 lg:grow lg:basis-0 lg:overflow-y-auto',
        isScrollable && !isAtTop && 'border-t-border-primary',
        isScrollable && !isAtBottom && 'border-b-border-primary'
      )}
      ref={listRef}
    >
      {orderRows.map((orderRow) => (
        <li
          className="border-border-primary mb-4 flex items-start gap-4 border-b pb-4 last:mb-0 last:border-none last:pb-0"
          key={orderRow?.rowSystemId}
        >
          <a href={orderRow?.url} className="w-1/4 shrink-0">
            <img
              className="aspect-square object-contain"
              src={orderRow?.image}
              alt={orderRow?.name}
            />
          </a>
          <div className="grid grow gap-4">
            <div className="body-sm leading-base flex min-w-0 items-start justify-between gap-4">
              <a
                href={orderRow?.url}
                className="link-inverted min-w-0 overflow-hidden text-ellipsis font-semibold"
              >
                {orderRow?.name}
              </a>
              <div className="whitespace-nowrap text-right">
                <div
                  className={cn(orderRow?.totalCampaignPrice && 'line-through')}
                >
                  {orderRow?.totalPrice}
                </div>
                {orderRow?.totalCampaignPrice && (
                  <div className="text-sale">
                    {orderRow?.totalCampaignPrice}
                  </div>
                )}
              </div>
            </div>
            {isBusinessCustomer && (
              <div className="body-sm leading-base -mb-1 -mt-2">
                {`${translate('checkout.cart.quantity-per-unit')}: ${orderRow?.quantityPerUnit || 1}`}
              </div>
            )}
            <div className="flex items-center justify-between gap-4">
              <div className="flex">
                <button
                  className={cn(
                    'border-border-primary flex size-9 items-center justify-center rounded-l-2xl border',
                    loading
                      ? 'disabled:cursor-wait'
                      : 'disabled:cursor-not-allowed'
                  )}
                  onClick={() => handleReduceQuantity(orderRow)}
                  disabled={loading || isSubmitting || orderRow?.quantity <= 1}
                  title={translate('checkout.cart.decrease_quantity')}
                >
                  <span
                    className={cn(
                      'icon !text-[1rem]',
                      orderRow?.quantity <= 1 && 'opacity-50'
                    )}
                  >
                    remove
                  </span>
                </button>
                <input
                  className={cn(
                    'no-number-spinner border-border-primary w-9 border-y text-center font-semibold disabled:cursor-wait',
                    loading
                      ? 'disabled:cursor-wait'
                      : 'disabled:cursor-not-allowed'
                  )}
                  type="number"
                  value={
                    editingQuantities[orderRow?.rowSystemId] ??
                    orderRow?.quantity
                  }
                  min={1}
                  maxLength={3}
                  onChange={(event) => {
                    const parsed = parseInt(event.target.value);
                    const value = isNaN(parsed) ? 0 : parsed;
                    setEditingQuantities((previous) => ({
                      ...previous,
                      [orderRow.rowSystemId]: value,
                    }));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.target.blur();
                    } else if (event.key === 'Escape') {
                      clearEditingQuantity(orderRow.rowSystemId);
                      setTimeout(() => event.target.blur(), 0);
                    }
                  }}
                  onBlur={() => {
                    const value = editingQuantities[orderRow?.rowSystemId];
                    if (value > 0) {
                      updateQuantity(orderRow, value);
                    } else {
                      clearEditingQuantity(orderRow.rowSystemId);
                    }
                  }}
                  disabled={loading || isSubmitting}
                />
                <button
                  className={cn(
                    'border-border-primary flex size-9 items-center justify-center rounded-r-2xl border',
                    loading
                      ? 'disabled:cursor-wait'
                      : 'disabled:cursor-not-allowed'
                  )}
                  onClick={() => handleIncreaseQuantity(orderRow)}
                  disabled={
                    loading ||
                    isSubmitting ||
                    orderRow?.quantity >= MAX_QUANTITY
                  }
                  title={translate('checkout.cart.increase_quantity')}
                >
                  <span
                    className={cn(
                      'icon !text-[1rem]',
                      orderRow?.quantity >= MAX_QUANTITY && 'opacity-50'
                    )}
                  >
                    add
                  </span>
                </button>
              </div>
              <button
                className={cn(
                  loading
                    ? 'disabled:cursor-wait'
                    : 'disabled:cursor-not-allowed'
                )}
                onClick={() => handleRemoveOrderRow(orderRow)}
                title={translate('checkout.cart.delete_order_row')}
                disabled={loading || isSubmitting}
              >
                <span className="icon !block !text-[1.25rem]">delete</span>
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default CheckoutCartItems;
